import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './2DColumnsPage.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TwoDColumnsPage = () => {
  const [gradesData, setGradesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGradesData();
  }, []);

  const fetchGradesData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/chart/grades`);
      if (!response.ok) {
        throw new Error('Failed to fetch grades data');
      }
      const data = await response.json();
      setGradesData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const chartData = {
    labels: gradesData.map(item => `Grade ${item.grade}`),
    datasets: [
      {
        label: 'Female Students',
        data: gradesData.map(item => item.female_count),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: '#667eea',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Male Students',
        data: gradesData.map(item => item.male_count),
        backgroundColor: 'rgba(240, 147, 251, 0.8)',
        borderColor: '#f093fb',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568',
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      title: {
        display: true,
        text: 'Student Grades Distribution by Gender',
        font: {
          size: 24,
          weight: 'bold',
          family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        },
        color: '#2d3748',
        padding: {
          top: 20,
          bottom: 30
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#2d3748',
        bodyColor: '#4a5568',
        borderColor: '#667eea',
        borderWidth: 2,
        cornerRadius: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        callbacks: {
          label: function(context) {
            const gender = context.dataset.label;
            const count = context.parsed.y;
            return `${gender}: ${count.toLocaleString()} students`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: '📊 Grade Level',
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568'
        },
        grid: {
          color: 'rgba(102, 126, 234, 0.1)',
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 13,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#718096'
        }
      },
      y: {
        title: {
          display: true,
          text: '👥 Number of Students',
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568'
        },
        grid: {
          color: 'rgba(102, 126, 234, 0.1)',
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 13,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#718096',
          callback: function(value) {
            return value.toLocaleString();
          },
          padding: 10
        },
        beginAtZero: true
      },
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  if (loading) {
    return (
      <div className="columns-page">
        <div className="columns-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading grades data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="columns-page">
        <div className="columns-container">
          <div className="error-state">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={fetchGradesData} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalStudents = gradesData.reduce((sum, item) => sum + item.female_count + item.male_count, 0);
  const totalFemale = gradesData.reduce((sum, item) => sum + item.female_count, 0);
  const totalMale = gradesData.reduce((sum, item) => sum + item.male_count, 0);

  return (
    <div className="columns-page">
      <div className="columns-container">
        <div className="chart-wrapper">
          <Bar data={chartData} options={options} />
        </div>
        
        {/* Summary Statistics */}
        <div className="chart-summary">
          <div className="summary-card">
            <h3>Total Students</h3>
            <p className="summary-number">
              {totalStudents.toLocaleString()}
            </p>
          </div>
          <div className="summary-card">
            <h3>Female Students</h3>
            <p className="summary-number">
              {totalFemale.toLocaleString()}
            </p>
          </div>
          <div className="summary-card">
            <h3>Male Students</h3>
            <p className="summary-number">
              {totalMale.toLocaleString()}
            </p>
          </div>
          <div className="summary-card">
            <h3>Gender Ratio</h3>
            <p className="summary-number">
              {((totalFemale / totalMale) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoDColumnsPage;
